// @tennis-matches/core/store/tournament
// Generic tournament store factory

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Factory function to create a tournament store
 * Can be used for any tournament type: schleifchen, clubmeisterschaft, medenspiele, etc.
 *
 * @param {Object} config - Tournament-specific configuration
 * @param {string} config.storeName - Store name for devtools (e.g., 'schleifchen')
 * @param {Object} config.defaultTournament - Default tournament settings
 * @param {Object} config.defaultPlayers - Default players settings
 * @param {Object} config.defaultUI - Default UI settings
 * @returns {Function} Zustand store hook
 */
export function createTournamentStore(config) {
  const {
    storeName = 'tournament',
    defaultTournament = {},
    defaultPlayers = { count: 12, names: {}, list: [] },
    defaultUI = {
      currentRound: 1,
      completedRound: 0,
      phase: 'idle',
      isSaving: false,
      isGenerating: false,
      error: null,
    },
  } = config;

  return create(
    devtools(
      persist(
        immer((set, get) => ({
          // ============================================
          // TOURNAMENT SLICE
          // ============================================
          tournament: {
            id: null,
            name: '',
            format: 'doubles',
            rounds: 3,
            minutesPerRound: 20,
            courts: { count: 3, numbers: ['1', '2', '3'] },
            points: { win: 2, draw: 1 },
            settings: {
              expressMode: true,
              autoAdvance: true,
              alwaysAskForRest: false,
              alwaysAskForChanges: false,
            },
            meta: {
              name: '',
              startDate: '',
              endDate: '',
              code: '',
              phase: 'planned',
            },
            resultRules: {
              mode: 'ONE_SET_OPEN',
              gamesToWinSet: 6,
              openSetWinByGames: 2,
              setWinByGames: 1,
              mtbPointsToWin: 10,
              mtbWinByPoints: 2,
              allowBeyond7: true,
            },
            dbPhase: null,
            internalId: null,
            ...defaultTournament,
          },

          setTournament: (tournament) =>
            set((state) => {
              state.tournament = { ...state.tournament, ...tournament };
            }),

          updateTournamentSettings: (settings) =>
            set((state) => {
              state.tournament.settings = {
                ...state.tournament.settings,
                ...settings,
              };
            }),

          updateTournamentMeta: (meta) =>
            set((state) => {
              state.tournament.meta = { ...state.tournament.meta, ...meta };
            }),

          updateResultRules: (resultRules) =>
            set((state) => {
              state.tournament.resultRules = {
                ...state.tournament.resultRules,
                ...resultRules,
              };
            }),

          // ============================================
          // SCHEDULE SLICE
          // ============================================
          schedule: [],

          setSchedule: (schedule) =>
            set((state) => {
              state.schedule = schedule;
            }),

          addRound: (round, index) =>
            set((state) => {
              while (state.schedule.length <= index) {
                state.schedule.push([]);
              }
              state.schedule[index] = round;
            }),

          updateRound: (roundIndex, round) =>
            set((state) => {
              if (roundIndex >= 0 && roundIndex < state.schedule.length) {
                state.schedule[roundIndex] = round;
              }
            }),

          // ============================================
          // RESULTS SLICE
          // ============================================
          results: {},

          setResult: (matchKey, result) =>
            set((state) => {
              state.results[matchKey] = result;
            }),

          setResults: (results) =>
            set((state) => {
              state.results = { ...state.results, ...results };
            }),

          clearResults: () =>
            set((state) => {
              state.results = {};
            }),

          updateResult: (roundKey, matchKey, result) =>
            set((state) => {
              const key = matchKey || `${roundKey}`;
              state.results[key] = result;
            }),

          // ============================================
          // PLAYERS SLICE
          // ============================================
          players: { ...defaultPlayers },

          setPlayers: (players) =>
            set((state) => {
              state.players = { ...state.players, ...players };
            }),

          setPlayerCount: (count) =>
            set((state) => {
              state.players.count = count;
            }),

          setPlayerNames: (names) =>
            set((state) => {
              state.players.names = names;
            }),

          setPlayerList: (list) =>
            set((state) => {
              state.players.list = list;
            }),

          // ============================================
          // UI SLICE
          // ============================================
          ui: { ...defaultUI },

          setCurrentRound: (round) =>
            set((state) => {
              state.ui.currentRound = round;
            }),

          setCompletedRound: (round) =>
            set((state) => {
              state.ui.completedRound = round;
            }),

          setPhase: (phase) =>
            set((state) => {
              state.ui.phase = phase;
            }),

          setIsSaving: (isSaving) =>
            set((state) => {
              state.ui.isSaving = isSaving;
            }),

          setIsGenerating: (isGenerating) =>
            set((state) => {
              state.ui.isGenerating = isGenerating;
            }),

          setError: (error) =>
            set((state) => {
              state.ui.error = error;
            }),

          clearError: () =>
            set((state) => {
              state.ui.error = null;
            }),

          setUI: (uiUpdate) =>
            set((state) => {
              state.ui = { ...state.ui, ...uiUpdate };
            }),

          // ============================================
          // PARTICIPATION STATUS SLICE
          // ============================================
          participationStatus: {},

          setParticipationStatus: (roundNo, status) =>
            set((state) => {
              state.participationStatus[roundNo] = status;
            }),

          setParticipationStatusForRound: (round, status) =>
            set((state) => {
              state.participationStatus[round] = {
                ...state.participationStatus[round],
                ...status,
              };
            }),

          // ============================================
          // UTILITY ACTIONS
          // ============================================

          loadFromDB: (data) => {
            console.log(`[${storeName} Store] loadFromDB called`, {
              hasTournament: !!data?.tournament,
              hasSchedule:
                Array.isArray(data?.schedule) && data.schedule.length > 0,
              scheduleLength: Array.isArray(data?.schedule) ? data.schedule.length : 0,
              hasPlayers: !!data?.players,
              playersCount: data?.players?.count || 0,
              dbUI: data?.ui,
            });
            
            // DEBUG: Log current state before update
            const currentState = get();
            console.log(`[${storeName} Store] Current state before loadFromDB:`, {
              scheduleLength: Array.isArray(currentState.schedule) ? currentState.schedule.length : 0,
              playersCount: currentState.players?.count || 0,
              localUI: currentState.ui,
            });

            set((state) => {
              if (data?.tournament) {
                state.tournament = { ...state.tournament, ...data.tournament };
                if (data.tournament.meta) {
                  state.tournament.meta = {
                    ...state.tournament.meta,
                    ...data.tournament.meta,
                  };
                }
              }
              // CRITICAL: Only update schedule if it has actual content
              // Prevents overwriting existing schedule with empty array from DB
              if (data?.schedule && Array.isArray(data.schedule) && data.schedule.length > 0) {
                state.schedule = data.schedule;
              }
              // CRITICAL: Only merge results if they have actual content
              // Prevents overwriting existing results with empty object
              if (data?.results && Object.keys(data.results || {}).length > 0) {
                state.results = { ...state.results, ...data.results };
              }
              // CRITICAL: Only update players if data.players has actual content
              // Prevents overwriting existing players with empty data from DB
              if (data?.players) {
                const hasCount = typeof data.players.count === 'number' && data.players.count > 0;
                const hasNames = data.players.names && Object.keys(data.players.names || {}).length > 0;
                const hasList = Array.isArray(data.players.list) && data.players.list.length > 0;
                
                // Only update if we have actual player data
                if (hasCount || hasNames || hasList) {
                  state.players = { ...state.players, ...data.players };
                }
              }
              if (data?.ui) {
                // CRITICAL: Don't overwrite currentRound if local state is newer
                // This prevents DB from overwriting a round that was just updated locally
                const localCurrentRound = state.ui?.currentRound || 0;
                const dbCurrentRound = data.ui?.currentRound || 0;
                const localCompletedRound = state.ui?.completedRound || 0;
                const dbCompletedRound = data.ui?.completedRound || 0;
                
                // CRITICAL: On refresh (localCurrentRound = 0), always use DB values
                // But verify that DB values are consistent: currentRound should be completedRound + 1
                // If not, something is wrong with the DB data
                const isRefresh = localCurrentRound === 0;
                const dbValuesConsistent = dbCurrentRound === dbCompletedRound + 1;
                
                // Use DB currentRound if:
                // 1. It's a refresh (local = 0), OR
                // 2. DB is exactly 1 higher than local (normal progression)
                // But if DB values are inconsistent, log a warning
                if (isRefresh && !dbValuesConsistent && dbCurrentRound > 0 && dbCompletedRound >= 0) {
                  console.warn(`[${storeName} Store] ⚠️ DB values inconsistent on refresh:`, {
                    dbCurrentRound,
                    dbCompletedRound,
                    expectedCurrentRound: dbCompletedRound + 1,
                    note: "DB current_round should be completed_round + 1",
                  });
                }
                
                const shouldUseDbCurrentRound =
                  isRefresh ||
                  dbCurrentRound > localCurrentRound ||
                  (dbCurrentRound === localCurrentRound + 1);
                
                // Only use DB completedRound if it's higher than local
                const shouldUseDbCompletedRound = dbCompletedRound > localCompletedRound;
                
                console.log(`[${storeName} Store] Merging UI state`, {
                  localCurrentRound,
                  dbCurrentRound,
                  shouldUseDbCurrentRound,
                  localCompletedRound,
                  dbCompletedRound,
                  shouldUseDbCompletedRound,
                  finalCurrentRound: shouldUseDbCurrentRound ? dbCurrentRound : localCurrentRound,
                  finalCompletedRound: shouldUseDbCompletedRound ? dbCompletedRound : localCompletedRound,
                });
                
                state.ui = { 
                  ...state.ui, 
                  ...data.ui,
                  // Preserve local currentRound if it's valid and newer
                  currentRound: shouldUseDbCurrentRound ? dbCurrentRound : localCurrentRound,
                  // Preserve local completedRound if it's higher
                  completedRound: shouldUseDbCompletedRound ? dbCompletedRound : localCompletedRound,
                };
              }
              if (data?.participationStatus) {
                state.participationStatus = {
                  ...state.participationStatus,
                  ...data.participationStatus,
                };
              }
            });

            const finalState = get();
            console.log(`[${storeName} Store] loadFromDB complete`, {
              tournament:
                finalState.tournament?.meta?.code ||
                finalState.tournament?.id ||
                'none',
              scheduleLength: Array.isArray(finalState.schedule) ? finalState.schedule.length : 0,
              playersCount: finalState.players?.count || 0,
              scheduleDetails: Array.isArray(finalState.schedule) ? finalState.schedule.map((round, idx) => ({
                roundIndex: idx,
                roundNo: idx + 1,
                matchCount: Array.isArray(round) ? round.length : 0
              })) : [],
            });
          },

          reset: () =>
            set((state) => {
              state.tournament = {
                id: null,
                name: '',
                format: 'doubles',
                rounds: 6,
                minutesPerRound: 15,
                courts: { count: 3, numbers: ['1', '2', '3'] },
                points: { win: 2, draw: 1 },
                settings: {
                  expressMode: true,
                  autoAdvance: true,
                  alwaysAskForRest: false,
                  alwaysAskForChanges: false,
                },
                meta: {
                  name: '',
                  startDate: '',
                  endDate: '',
                  code: '',
                  phase: 'planned',
                },
                resultRules: {
                  mode: 'ONE_SET_OPEN',
                  gamesToWinSet: 6,
                  openSetWinByGames: 2,
                  setWinByGames: 1,
                  mtbPointsToWin: 10,
                  mtbWinByPoints: 2,
                  allowBeyond7: true,
                },
                dbPhase: null,
                internalId: null,
                ...defaultTournament,
              };
              state.schedule = [];
              state.results = {};
              state.players = { ...defaultPlayers };
              state.ui = { ...defaultUI };
              state.participationStatus = {};
            }),

          resetTournament: () => {
            get().reset();
          },

          getFullState: () => {
            const state = get();
            return {
              app: { locale: 'de-DE' },
              shared: {
                players: state.players,
                timerPrefs: {
                  preCountdownSec: 5,
                  speakLang: 'de-DE',
                  beepOnStart: true,
                  beepOneMinute: true,
                  countLast10: true,
                },
              },
              modules: {
                [storeName]: {
                  tournament: state.tournament,
                  schedule: state.schedule,
                  results: state.results,
                  ui: {
                    round: state.ui.currentRound,
                    completedRound: state.ui.completedRound,
                    phase: state.ui.phase,
                    scheduleOverviewMode: state.ui?.scheduleOverviewMode ?? 'single',
                    scheduleOverviewOpenGroup: state.ui?.scheduleOverviewOpenGroup ?? 'current',
                  },
                  participationStatus: state.participationStatus,
                  resultRules: state.tournament.resultRules,
                },
              },
              timer: {
                remainingSec: null,
                isRunning: false,
                isPaused: false,
                mode: 'round',
              },
            };
          },
        })),
        { name: `${storeName}-storage` }
      ),
      { name: storeName }
    )
  );
}
